import { apiRequest } from '@/utils/api-request';
import * as Sentry from '@sentry/react';

// Mirrors the workflow's get_detailed_status().pending_clarifications shape.
export interface PendingClarificationEntry {
  node_id: string;
  display_name: string;
  question: string;
  context: string | null;
  /** Absolute deadline (epoch ms) the timer fires at. ``null`` only
   *  when the request surfaces before the wait stamped it. */
  deadline_epoch_ms: number | null;
  /** Workflow timeout (secs). Fallback countdown anchor when
   *  ``deadline_epoch_ms`` is missing. ``null`` for "ask and wait"
   *  (indefinite — no countdown). Optional: an older BE may omit the
   *  field, which callers treat as the default (``?? 60``). */
  timeout_secs?: number | null;
}

export interface PendingClarificationsResponse {
  session_id: string;
  pending_clarifications: PendingClarificationEntry[];
  // Lets session-mount render the frozen visual without waiting for a WS event.
  is_paused?: boolean;
  paused_at_epoch_ms?: number | null;
}

/**
 * Fetch the workflow's currently pending clarifications.
 *
 * Called on session mount so a refresh mid-clarification rehydrates the chat
 * panel. An empty list means the FE must not enter clarification-mode.
 */
export const getPendingClarifications = async (
  workflowId: string,
  sessionId: string,
  orgId: string,
): Promise<PendingClarificationsResponse> => {
  try {
    // ``apiRequest`` does NOT throw on HTTP errors — it resolves to
    // ``{ detail, success: false }`` on non-2xx and ``{ data: <body> }``
    // on success. A naive cast left ``pending_clarifications`` undefined
    // (the array lives under ``response.data``), silently breaking
    // reload-recovery. Unwrap explicitly and throw on the error path.
    const response = await apiRequest<PendingClarificationsResponse>({
      url: `/api/v1/orgs/${orgId}/workflows/${workflowId}/runs/session/${sessionId}/pending-clarifications`,
      method: 'GET',
      target: 'workflow',
    });
    if (response?.detail) {
      throw new Error(response.detail);
    }
    // Defensive ``response.data ?? response`` (matches
    // ``get-run-by-session.ts``): resilient if a future route returns
    // the body flat instead of wrapped.
    const data = (response?.data ?? response) as
      | PendingClarificationsResponse
      | undefined;
    if (!data || !Array.isArray(data.pending_clarifications)) {
      // Unexpected shape — treat as "no pending clarifications" so the
      // reload-recovery effect proceeds with an empty queue instead of
      // crashing on ``.length``; capture the shape for contract fixes.
      Sentry.captureMessage(
        'pending-clarifications: unexpected response shape',
        {
          level: 'warning',
          extra: { workflowId, sessionId, response },
        },
      );
      return { session_id: sessionId, pending_clarifications: [] };
    }
    return data;
  } catch (error) {
    Sentry.captureException(error, {
      extra: {
        context: 'get-pending-clarifications',
        workflowId,
        sessionId,
      },
    });
    throw error;
  }
};
